import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { EXPERTISE, GEAR, T } from "@/data/content";
import { FadeUp, MaskLine, SectionLabel } from "@/components/Reveal";
import { Aperture, Battery, Camera, Lightbulb, Plane } from "lucide-react";

const GEAR_ICONS = {
    camera: Camera,
    aperture: Aperture,
    battery: Battery,
    plane: Plane,
    lightbulb: Lightbulb,
};

export default function About() {
    const { lang } = useLang();
    const portraitRef = useRef(null);
    const secRef = useRef(null);
    const { scrollYProgress: wmProg } = useScroll({
        target: secRef,
        offset: ["start end", "end start"],
    });
    const awy = useTransform(wmProg, [0, 1], ["10%", "-8%"]);
    const { scrollYProgress } = useScroll({
        target: portraitRef,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [-36, 36]);

    const words = tx(lang, T.about.statement);

    return (
        <section
            id="about"
            ref={secRef}
            data-testid="about-section"
            className="relative overflow-hidden bg-ink py-24 text-cream md:py-36"
        >
            <div
                className="pointer-events-none absolute inset-0 opacity-30"
                style={{
                    background:
                        "radial-gradient(45% 40% at 12% 80%, rgba(138,43,226,0.16) 0%, transparent 70%)",
                }}
            />
            <motion.div
                aria-hidden="true"
                data-testid="about-watermark"
                style={{ y: awy }}
                className="pointer-events-none absolute -left-[8%] bottom-[4%] z-0 select-none font-display text-[40vw] font-extrabold leading-none text-outline opacity-60 md:text-[28rem]"
            >
                G.
            </motion.div>
            <div className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10">
                <SectionLabel en={tx(lang, T.about.label)} fr={tx(lang, T.about.label)} />

                <div className="mt-10 grid grid-cols-1 gap-14 md:mt-14 md:grid-cols-12 md:gap-10">
                    <div className="md:col-span-7 lg:col-span-8">
                        <h3 className="font-display text-[clamp(1.6rem,3.4vw,3.2rem)] font-bold leading-[1.15] tracking-tight">
                            {words.split("—").map((part, i) => (
                                <MaskLine key={i} delay={i * 0.12} className="mt-1">
                                    <span>
                                        {i === 1 ? (
                                            <span className="font-serifit italic text-violet">
                                                {part}
                                            </span>
                                        ) : (
                                            part
                                        )}
                                        {i === 0 ? " —" : ""}
                                    </span>
                                </MaskLine>
                            ))}
                        </h3>

                        <FadeUp delay={0.15}>
                            <p className="mt-10 max-w-xl text-base font-light leading-relaxed text-cream/60 md:text-lg">
                                {tx(lang, T.about.body)}
                            </p>
                        </FadeUp>

                        <FadeUp delay={0.25}>
                            <div className="mt-10 flex flex-wrap gap-2 md:gap-3">
                                {EXPERTISE.map((e) => (
                                    <span
                                        key={e.en}
                                        data-testid="about-expertise-chip"
                                        className="rounded-full border border-white/15 px-4 py-2 font-mono text-[9px] tracking-[0.25em] text-cream/70 transition-colors duration-300 hover:border-violet hover:text-cream md:text-[10px]"
                                    >
                                        {lang === "fr" ? e.fr : e.en}
                                    </span>
                                ))}
                            </div>
                        </FadeUp>
                    </div>

                    <div className="md:col-span-5 lg:col-span-4">
                        <FadeUp delay={0.2}>
                            <div
                                ref={portraitRef}
                                className="relative aspect-[3/4] overflow-hidden border border-white/10"
                                data-testid="about-portrait"
                            >
                                <motion.img
                                    src="/img/about-camera.jpg"
                                    alt="Sony A7R V — SmallRig"
                                    loading="lazy"
                                    style={{ y }}
                                    className="absolute inset-0 h-full w-full scale-[1.15] object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                                <p className="absolute bottom-4 left-4 font-mono text-[9px] tracking-[0.3em] text-cream/80 md:text-[10px]">
                                    {tx(lang, T.about.portraitCaption)}
                                </p>
                            </div>
                        </FadeUp>
                    </div>
                </div>



                {/* gear — l'arsenal */}
                <div
                    className="mt-16 border-t border-white/10 pt-10 md:mt-24 md:pt-14"
                    data-testid="gear-section"
                >
                    <FadeUp delay={0.05}>
                        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
                            <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight md:text-6xl">
                                {tx(lang, T.about.gear)}
                                <span className="text-violet">.</span>
                            </h3>
                            <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                                {tx(lang, T.about.gearNote)}
                            </p>
                        </div>
                    </FadeUp>
                    <div className="mt-10 md:mt-14">
                        <div className="grid grid-cols-1 gap-x-10 gap-y-9 sm:grid-cols-2 lg:grid-cols-3">
                            {GEAR.map((g, gi) => {
                                const Icon = GEAR_ICONS[g.icon];
                                return (
                                    <FadeUp key={g.group.en} delay={0.08 + gi * 0.06}>
                                        <div data-testid={`gear-group-${g.icon}`}>
                                            <div className="flex items-center gap-3 border-b border-white/10 pb-3">
                                                <Icon size={14} className="text-violet" />
                                                <p className="font-mono text-[10px] tracking-[0.3em] text-cream/60">
                                                    {tx(lang, g.group)}
                                                </p>
                                            </div>
                                            <ul className="mt-4 space-y-3">
                                                {g.items.map((it) => (
                                                    <li
                                                        key={it.name}
                                                        className="group flex items-baseline justify-between gap-4"
                                                    >
                                                        <span className="font-display text-base font-bold uppercase tracking-tight text-cream transition-colors duration-300 group-hover:text-violet md:text-lg">
                                                            {it.name}
                                                        </span>
                                                        <span className="shrink-0 text-right font-mono text-[8px] tracking-[0.2em] text-cream/40 md:text-[9px]">
                                                            {tx(lang, it.spec)}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </FadeUp>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
